/**
 * Date utilities for handling date-only values without timezone conversion
 */

/**
 * Format a date string (YYYY-MM-DD) as a localized date without timezone conversion
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateOnly = (dateString, options = {}) => {
  if (!dateString) return 'Not set';
  
  try {
    // Parse date string as date-only (no timezone conversion)
    // Input format: "YYYY-MM-DD"
    const [year, month, day] = dateString.split('-').map(Number);
    
    // Create date object using local timezone constructor (no UTC conversion)
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };
    
    return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
  } catch (error) {
    console.warn('Invalid date string:', dateString);
    return dateString; // Return original if parsing fails
  }
};

/**
 * Format a date string for short display (e.g., "Aug 25, 2025")
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateString) => {
  return formatDateOnly(dateString, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format a date string for long display (e.g., "Monday, August 25, 2025")
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
export const formatDateLong = (dateString) => {
  return formatDateOnly(dateString, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format a datetime string for display
 * @param {string} dateTimeString - ISO datetime string
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'Not set';
  
  return new Date(dateTimeString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

/**
 * A task's `due_time` as a sentence — "1:35 AM", never "01:35:00".
 *
 * ⚠️ IT IS A NAIVE LOCAL TIME ON THE ROW AND THERE IS NOTHING TO
 * CONVERT (Cowork run 20). `TaskInstance.due_time` is a `TimeField`
 * written from a schedule's own `due_by_hour`/`due_by_minute` — the hour
 * the operator typed — so the only defect was the DISPLAY: the two task
 * cards printed the serialized `HH:MM:SS` verbatim while every other
 * surface in the app (the AI payloads, the planner's prompt, the
 * schedules table) renders it `%H:%M`, and a machine string with
 * trailing seconds on a card reads as a UTC stamp somebody forgot to
 * localise.
 *
 * Built with the LOCAL constructor on an arbitrary date, like
 * `formatDateOnly` — `new Date('1970-01-01T01:35:00')` would be read as
 * UTC by some engines and shift the hour it is here to preserve.
 *
 * Returns '' for anything that is not a time, so a card renders no
 * clause rather than "at Invalid Date".
 */
export const formatTimeOnly = (timeString) => {
  const parts = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(
    String(timeString || '').trim());
  if (!parts) return '';
  const hours = Number(parts[1]);
  const minutes = Number(parts[2]);
  if (hours > 23 || minutes > 59) return '';
  const when = new Date(2000, 0, 1, hours, minutes);
  return when.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * The "Due: …" clause both task cards print — ONE implementation, so
 * Tasks Today and All Tasks cannot describe one card two ways.
 *
 * `due_date` is a bare `YYYY-MM-DD` and goes through `formatDateOnly`,
 * which parses it with the LOCAL constructor: `new Date('2026-09-26')`
 * is UTC midnight and reads a day early west of Greenwich, which is the
 * tz-sweep rule this whole module exists for.
 */
export const formatDueDisplay = (dueDate, dueTime) => {
  if (!dueDate) return '';
  const when = formatDateOnly(dueDate);
  const at = formatTimeOnly(dueTime);
  // ⚠️ A DATE WE CANNOT READ PRINTS NOTHING, NEVER "Invalid Date"
  // (review of run 20's pass). `formatDateOnly` splits on "-" and hands
  // the pieces to the local constructor, so an ISO DATETIME
  // ('2026-09-26T13:00:00Z' — what every other field on the row is) and
  // any junk both land on an Invalid Date and printed
  // "Invalid Date at 1:35 AM" on the card, which reads as a bug in the
  // task rather than as a field we could not parse. The TIME is still a
  // fact we have, so it is printed alone; with neither there is no
  // clause, the same answer `!dueDate` gives.
  if (!when || when === 'Invalid Date') return at;
  return at ? `${when} at ${at}` : when;
};

/**
 * The calendar day the OPERATOR is in, as `YYYY-MM-DD` — never
 * `toISOString().slice(0, 10)`, which is the UTC day.
 *
 * I-03 R4 · S3-04.2 review round 3 (BLOCK): the Pairings row's "End
 * today" built its day with `toISOString()`, so pressed after 18:00
 * CST (19:00 CDT) it recorded TOMORROW — and the server, handed a bare
 * day over a NULL `ended_at`, stored exactly that at local midnight.
 * Same defect class as round 2's server-side rung (two surfaces
 * disagreeing about one date), from the other side. This is the one
 * derivation; the server's `settings.TIME_ZONE` is the operator's zone
 * and the browser's local clock is the same zone on the operator's own
 * devices, which is the premise every date editor on the row shares
 * (`dayOf` slices the server's local rendering).
 */
export const localDay = (date = new Date()) => {
  const two = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}`;
};

/**
 * The zone the API renders and judges dates in — Django's
 * `settings.TIME_ZONE`. Written out here because the client cannot ask
 * for it; a change to the server's zone has to change this line too.
 */
export const API_TIME_ZONE = 'America/Chicago';

/**
 * TODAY AS THE API SEES IT, as `YYYY-MM-DD` (run-23 closure, ruling 5).
 *
 * A pairing's dates are rendered by the API in `settings.TIME_ZONE` —
 * the Pairings row slices that rendering — and the server refuses a day
 * later than its own today. `localDay()` is the BROWSER's day, which is
 * the same day only on a device in Chicago's zone: from a browser east
 * of Chicago, between its midnight and Chicago's, "End today" sent a day
 * the server reads as the future, and the row called yesterday's end
 * "Ended today". This is the ONE derivation for both — the day End today
 * sends and the day "Ended today" compares against — so the two can
 * never disagree about which day "today" is. The server rule stays
 * strict: a future day is still refused.
 */
export const apiDay = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: API_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const part = (type) => (parts.find((p) => p.type === type) || {}).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};
